import { useState, useEffect } from "react";
import api from "../../utils/api";

export default function AddUser({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("");
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [currentRole, setCurrentRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get("/products", { withCredentials: true });
        setAllProducts(res.data.products || res.data);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      }
    };
    fetchProducts();
    const fetchCurrent = async () => {
      try {
        const res = await api.get("/users/me", { withCredentials: true });
        setCurrentRole(res.data.role);
        setCurrentUserId(res.data.id || res.data._id || null);
      } catch (err) {
        console.error("Failed to fetch current user:", err);
      }
    };
    fetchCurrent();
  }, []);

  useEffect(() => {
    if (role === "Admin") {
      const allProductNames = allProducts.map((p) => p.name);
      setSelectedProducts(allProductNames);
    } else {
      setSelectedProducts([]);
    }
  }, [role, allProducts]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username) {
      alert("Username is required.");
      return;
    }

    if (!email) {
      alert("Email is required.");
      return;
    }

    if (!password) {
      alert("Password is required.");
      return;
    }

    if (!role) {
      alert("Role is required.");
      return;
    }

    try {
      const payload = {
        username,
        email,
        password,
        role,
        product_list: selectedProducts,
      };

      if (currentUserId) payload.createdBy = currentUserId;

      await api.post("/users", payload, { withCredentials: true });

      alert("User added successfully!");
      setUsername("");
      setEmail("");
      setPassword("");
      setRole("");
      setSelectedProducts([]);

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error(error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert("Something went wrong");
      }
    }
  };

  const handleProductToggle = (productName) => {
    setSelectedProducts((prev) =>
      prev.includes(productName)
        ? prev.filter((p) => p !== productName)
        : [...prev, productName],
    );
  };

  const handleSelectAll = () => {
    setSelectedProducts(allProducts.map((p) => p.name));
  };

  const handleDeselectAll = () => {
    setSelectedProducts([]);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm border p-6"
    >
      <h2 className="text-xl font-semibold mb-6">Add User</h2>

      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">Role</label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="w-full rounded-lg border px-3 py-2"
        >
          <option value="" disabled>
            Select role
          </option>
          {(() => {
            const available =
              currentRole === "Admin" ? ["Admin", "Manager", "User"] : ["User"];
            return available.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ));
          })()}
        </select>
      </div>

      {/* User Details - Show when role is selected */}
      {role && (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Username</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-lg border px-3 py-2"
              placeholder="Username"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border px-3 py-2"
              placeholder="Email"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border px-3 py-2"
              placeholder="Password"
            />
          </div>

          {/* <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-lg border px-3 py-2"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div> */}

          {/* Products - Show only if not Admin */}
          {role !== "Admin" && (
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Products</label>
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={handleDeselectAll}
                  className="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600 transition"
                >
                  Deselect All
                </button>
              </div>
              <div className="flex flex-wrap gap-3 max-h-64 overflow-y-auto border rounded-lg p-3">
                {allProducts.map((product) => (
                  <label
                    key={product._id}
                    className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-100 transition"
                  >
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.name)}
                      onChange={() => handleProductToggle(product.name)}
                    />
                    {product.imageUrl && (
                      <img
                        src={`http://localhost:4000/${product.imageUrl}`}
                        alt={product.name}
                        className="w-6 h-6 object-cover rounded"
                      />
                    )}
                    <span>{product.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-6">
            <button
              type="submit"
              className="w-full rounded-lg bg-green-500 py-2.5 text-white font-medium
               hover:bg-green-700 transition"
            >
              Create User
            </button>
          </div>
        </>
      )}
    </form>
  );
}
